import { useState, useEffect, useRef, memo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged, signOut, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getFirestore, doc, setDoc, onSnapshot, getDoc, serverTimestamp } from 'firebase/firestore';
import { User, Compass, Target, FileText, BarChart2, MessageSquare, LogOut, XCircle, UploadCloud, Sparkles, MapPin, Brain, Play, HelpCircle, Users, Settings, Moon, Sun, Edit3, Lightbulb, Tag } from 'lucide-react';
import rakfort from '../src/rakfort.jpg';
import ResumeBuilder from './ResumeBuilder';
import CareerPath from './CareerPath';
import JobMatching from './JobMatching';
import CoverLetterGenerator from './CoverLetterGenerator';
import ResumeScoring from './ResumeScoring';
import InterviewPrep from './InterviewPrep';
import Dashboard from './dashboard';
import Layout from './components/common/Layout';
import Auth from './auth';
import Playground from './Playground';
import InterviewQuestions from './InterviewQuestions';
import SignUp from './pages/SignUp';
import Pricing from './Pricing';
import LandingPage from './landing/Landing';
import StudyLayout from './components/common/StudyLayout';
import PrivacyPolicy from './PrivacyPolicy';
import { NoviContact } from './components/NoviContact';
import TermsConditionsMain from './TermsConditionsMain';
import CancellationRefund from './CancellationRefund';
//import ContactPage from './pages/ContactPage';

// Import new Desktop screens
import Desktop4 from './screens/Desktop4';
import Desktop20 from './screens/Desktop20';
//import Pricing from './pages/SignupPricing';
import Desktop21 from './screens/Desktop21';
import Desktop22 from './screens/Desktop22';
import Desktop23 from './screens/Desktop23';
import Desktop24 from './screens/Desktop24';
import Desktop25 from './screens/Desktop25';
import Desktop26 from './screens/Desktop26';
//import Desktop27 from './screens/Desktop27';
import Desktop28 from './screens/Desktop28';
import Desktop29 from './screens/Desktop29';
import Desktop30 from './screens/Desktop30';
import SignupPricing from './pages/SignupPricing';
import Blogs from './pages/Blogs';
import BlogDetail from './pages/BlogDetail';
import Terms from './pages/Terms';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";


// ---------------------- THEME ----------------------
// Allowed colors only:rgb(177, 123, 218) (primary), #fafafa (surface), #d5e7f3 (accent)
// Tailwind classes use arbitrary color values to strictly adhere to the palette.

const firebaseConfig = {
  apiKey: "AIzaSyCk3JSOcoaToCE2VIApkMdzDQEFmmqsCGU",
  authDomain: "aspirant-suite-project.firebaseapp.com",
  projectId: "aspirant-suite-project",
  storageBucket: "aspirant-suite-project.firebasestorage.app",
  messagingSenderId: "283579018618",
  appId: "1:283579018618:web:bcb2e6277e8b0s57f517ac3",
  measurementId: "G-2RETHT94ES"
};

const appId = 'aspirant-suite';
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// Deep sanitize to avoid undefined in Firestore
const deepSanitize = (value) => {
  if (value === undefined || value === null) return Array.isArray(value) ? [] : (typeof value === 'string' ? '' : (typeof value === 'number' ? 0 : (typeof value === 'boolean' ? false : (Array.isArray(value) ? [] : (typeof value === 'object' ? {} : '')))));
  if (Array.isArray(value)) return value.map((v) => deepSanitize(v)).filter((v) => v !== undefined);
  if (typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach((k) => {
      const v = deepSanitize(value[k]);
      if (v === undefined) {
        out[k] = '';
      } else {
        out[k] = v;
      }
    });
    return out;
  }
  return value;
};

// Strip basic markdown/bullets for clean display
const stripMarkdown = (text) => {
  if (typeof text !== 'string') return text;
  return text
    .replace(/\*\*/g, '') // Remove bold
    .replace(/\*/g, '') // Remove italic
    .replace(/`{1,3}/g, '') // Remove code blocks
    .replace(/^\s*[-•]\s+/gm, '') // Remove bullet points
    .replace(/^\s*#+\s*/gm, '') // Remove headers
    .replace(/^\s*[-=]+\s*$/gm, '') // Remove horizontal rules
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Convert links to text
    .replace(/^\s*>\s*/gm, '') // Remove blockquotes
    .replace(/\n{3,}/g, '\n\n') // Limit multiple newlines
    .trim();
};

const formatResumeDataAsText = (data) => {
  if (!data || !data.personalInfo) return "";
  let text = `Name: ${data.personalInfo.name}\nEmail: ${data.personalInfo.email}\nPhone: ${data.personalInfo.phone}\nLocation: ${data.personalInfo.location}\n\n`;
  if (data.goal) text += `## Goal\n${data.goal}\n\n`;

  text += "## Work Experience\n";
  (data.experience || []).forEach(exp => {
    text += `- ${exp.title} at ${exp.company} (${exp.period})\n ${exp.description}\n`;
  });

  text += "\n## Education\n";
  (data.education || []).forEach(edu => {
    text += `- ${edu.degree} from ${edu.school} (${edu.period})\n ${edu.description}\n`;
  });

  text += "\n## Projects\n";
  (data.projects || []).forEach(proj => {
    text += `- ${proj.title} (${proj.period})\n ${proj.description}\n`;
  });

  text += "\n## Skills\n";
  {
    const skills = Array.isArray(data.skills)
      ? data.skills.filter(Boolean).map(String)
      : (typeof data.skills === 'string' ? data.skills.split(',').map(s => s.trim()).filter(Boolean) : []);
    text += skills.join(', ');
  }

  text += "\n\n## Achievements\n";
  (data.achievements || []).forEach(ach => {
    text += `- ${ach.description}\n`;
  });

  text += "\n## Awards\n";
  (data.awards || []).forEach(award => {
    text += `- ${award.description}\n`;
  });

  text += "\n## Certificates\n";
  (data.certificates || []).forEach(cert => {
    text += `- ${cert.description}\n`;
  });

  text += "\n## Hobbies\n";
  (data.hobbies || []).forEach(hobby => {
    text += `- ${hobby.description}\n`;
  });

  return text;
};

// MainContent hoisted to module scope to preserve identity across AppContent re-renders
const MainContent = memo(function MainContent({
  activeTab,
  resumeData,
  userData,
  db,
  appId,
  analysisTrigger,
  coverLetterJd,
  interviewQuestionsJd,
  onDataChange,
  onAnalyze,
  onParseComplete,
  onCareerTabChange,
  onGenerateCoverLetter,
  onGenerateInterviewQuestions,
  onClearCoverLetterJd,
  setActiveTab,
  user
}) {
  // Use the onGenerate* props directly instead of handle* props
  const handleInitiateCoverLetter = onGenerateCoverLetter;
  const handleInitiateInterviewQuestions = onGenerateInterviewQuestions;
  const handleClearCoverLetterJd = onClearCoverLetterJd;
  if (!resumeData) return null; // Match previous behavior: render nothing until resume data is ready
  switch (activeTab) {
    case 'resumeBuilder':
      return (
        <ResumeBuilder
          data={resumeData}
          onDataChange={onDataChange}
          onAnalyze={onAnalyze}
          onParseComplete={onParseComplete}
        />
      );
    case 'careerPath':
      return (
        <CareerPath
          user={user}
          userData={userData}
          resumeData={resumeData}
          db={db}
          appId={appId}
          externalActiveTab="basic"
          onTabChange={onCareerTabChange}
          showOnlyBasic={true}
        />
      );
    case 'careerCopilot':
      return (
        <CareerPath
          user={user}
          userData={userData}
          resumeData={resumeData}
          db={db}
          appId={appId}
          externalActiveTab="advanced"
          showOnlyAdvanced={true}
          onTabChange={onCareerTabChange}
        />
      );
    case 'jobMatching':
      return (
        <JobMatching
          resumeData={resumeData}
          onGenerateCoverLetter={handleInitiateCoverLetter}
          onGenerateInterviewQuestions={handleInitiateInterviewQuestions}
          setActiveTab={setActiveTab}
          user={user}
        />
      );
    case 'pricing':
      return (
        <Pricing
          resumeData={resumeData}
          onGenerateCoverLetter={handleInitiateCoverLetter}
          onGenerateInterviewQuestions={handleInitiateInterviewQuestions}
          setActiveTab={setActiveTab}
          user={user}
        />
      );
    case 'coverLetter':
      return (
        <CoverLetterGenerator
          user={user}
          resumeData={resumeData}
          initialJobDescription={coverLetterJd}
          onClearInitialJd={onClearCoverLetterJd}
          db={db}
          appId={appId}
        />
      );
    case 'resumeScoring':
      return (
        <ResumeScoring
          user={user}
          builderData={resumeData}
          trigger={analysisTrigger}
          db={db}
          appId={appId}
        />
      );
    case 'interviewPrep':
      return (
        <InterviewPrep
          user={user}
          userData={userData}
          resumeData={resumeData}
          db={db}
          appId={appId}
        />
      );
    case 'dashboard':
      return (
        <Dashboard
          user={user}
          userData={userData}
          resumeData={resumeData}
          db={db}
          appId={appId}
          onGenerateCoverLetter={onGenerateCoverLetter}
          onGenerateInterviewQuestions={handleInitiateInterviewQuestions}
          setActiveTab={setActiveTab}
        />
      );
    case 'playground':
      return <Playground resumeData={resumeData} />;
    case 'interviewQuestions':
      return <InterviewQuestions resumeData={resumeData} jobDescription={interviewQuestionsJd} />;
    case 'mapAnalysis':
      return (
        <CareerPath
          user={user}
          userData={userData}
          resumeData={resumeData}
          db={db}
          appId={appId}
          externalActiveTab="basic"
          showOnlyBasic={true}
        />
      );
    default:
      return (
        <ResumeBuilder
          data={resumeData}
          onDataChange={onDataChange}
          onAnalyze={onAnalyze}
          onParseComplete={onParseComplete}
        />
      );
  }
});

// ---------------------- UI ----------------------

const LoginScreen = ({ onSignIn, onSkip }) => (
  <Auth onSignIn={onSignIn} />
);

const ResumeUploadModal = ({ isOpen, onUploadComplete, onParseComplete, onSignOut }) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isParsing, setIsParsing] = useState(false);
  const [error, setError] = useState(null);
  const [resumeContent, setResumeContent] = useState('');

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      setError(null);
    }
  };

  const handleParseResume = async () => {
    const hasText = typeof resumeContent === 'string' && resumeContent.trim().length > 0;
    const hasFile = !!uploadedFile;
    if (!hasFile && !hasText) {
      setError("Please upload a file or paste your resume content.");
      return;
    }
    setIsParsing(true);
    setError(null);

    const formData = new FormData();
    if (hasFile) {
      formData.append('resume_file', uploadedFile);
    }
    // Include optional pasted resume content as plain text if provided
    if (hasText) {
      formData.append('resume_text', resumeContent.trim());
    }

    try {
      const response = await fetch(`${API_BASE_URL}/parse-resume`, {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Failed to parse resume.');
      }
      const parsedData = await response.json();
      await onParseComplete(parsedData);
      onUploadComplete();
    } catch (err) {
      console.error("Error parsing resume:", err);
      setError(err.message);
    } finally {
      setIsParsing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[#3C0F6C]/80 flex items-center justify-center p-4 z-50">
      <div className="text-center p-8 bg-[#fafafa] border border-[#d5e7f3] rounded-2xl shadow-2xl max-w-lg w-full">
        <UploadCloud size={48} className="mx-auto text-[#3C0F6C] mb-4" />
        <h1 className="text-3xl font-bold text-[#3C0F6C] mb-2">Welcome to Aspirant Suite</h1>
        <p className="text-sm text-[#3C0F6C]/80 mb-6">To get started, please upload your resume (PDF or DOCX).</p>

        {error && (
          <p className="text-sm text-[#3C0F6C] bg-[#d5e7f3] p-3 rounded-xl mb-4">{error}</p>
        )}

        <div className="mb-6 text-left">
          <input
            id="resume-upload"
            type="file"
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx"
            className="block w-full text-sm text-[#3C0F6C] file:mr-4 file:py-2 file:px-4 file:rounded-full file:border file:border-[#d5e7f3] file:text-sm file:font-semibold file:bg-[#d5e7f3] file:text-[#3C0F6C] hover:file:bg-[#fafafa]"
          />
          {uploadedFile && (
            <p className="mt-2 text-xs text-[#3C0F6C]/80">Selected: {uploadedFile.name}</p>
          )}
        </div>

        {/* Optional pasted resume content textarea */}
        <div className="mb-6 text-left">
          <label htmlFor="resume-content" className="block text-sm font-medium text-[#3C0F6C] mb-2">
            Paste your resume content here…
          </label>
          <textarea
            id="resume-content"
            rows={6}
            value={resumeContent}
            onChange={(e) => setResumeContent(e.target.value)}
            placeholder="Paste your resume content here…"
            className="w-full text-sm text-[#3C0F6C] bg-[#fafafa] border border-[#d5e7f3] rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-[#d5e7f3] placeholder:text-[#3C0F6C]/60"
          />
        </div>

        <button
          onClick={handleParseResume}
          disabled={((!uploadedFile && !(resumeContent && resumeContent.trim().length > 0)) || isParsing)}
          className="w-full bg-[#3C0F6C] text-[#fafafa] font-semibold py-3 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#3C0F6C]/90"
        >
          {isParsing ? (
            <svg className="animate-spin h-5 w-5 text-[#fafafa]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <Sparkles size={18} />
          )}
          <span>{isParsing ? 'Analyzing Your Resume...' : 'Upload & Continue'}</span>
        </button>

        <button onClick={onSignOut} className="mt-4 text-xs text-[#3C0F6C]/80 hover:text-[#3C0F6C]">Sign Out</button>
      </div>
    </div>
  );
};

const AppContent = () => {
  const [activeTab, setActiveTab] = useState('resumeBuilder');
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [resumeData, setResumeData] = useState(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [analysisTrigger, setAnalysisTrigger] = useState({ source: null, timestamp: null });
  const [coverLetterJd, setCoverLetterJd] = useState('');
  const [interviewQuestionsJd, setInterviewQuestionsJd] = useState('');
  const [isInterviewQuestionsModalOpen, setIsInterviewQuestionsModalOpen] = useState(false);
  const [interviewQuestionsData, setInterviewQuestionsData] = useState(null);
  const [isLoadingInterviewQuestions, setIsLoadingInterviewQuestions] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const stored = localStorage.getItem('theme');
      if (stored) return stored === 'dark';
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch { return false; }
  });
  // Persist and apply theme (must be declared before any early returns)
  useEffect(() => {
    try {
      document.documentElement.classList.toggle('dark', isDarkMode);
      localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    } catch { }
  }, [isDarkMode]);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [careerActiveTab, setCareerActiveTab] = useState('basic');
  const [guestMode, setGuestMode] = useState(() => {
    try { return localStorage.getItem('guestMode') === '1'; } catch { return false; }
  });
  const [isSidebarPinnedOpen, setIsSidebarPinnedOpen] = useState(() => {
    try { return localStorage.getItem('sidebarPinned') === '1'; } catch { return false; }
  });
  // Hover-expand drawer state with delays to prevent flicker
  const [isHovered, setIsHovered] = useState(false);
  const hoverTimers = useRef({ enter: null, leave: null });
  const isDrawerOpen = (isSidebarPinnedOpen || isHovered);

  const handleSidebarMouseEnter = () => {
    if (hoverTimers.current.leave) {
      clearTimeout(hoverTimers.current.leave);
      hoverTimers.current.leave = null;
    }
    hoverTimers.current.enter = setTimeout(() => setIsHovered(true), 150);
  };

  const handleSidebarMouseLeave = () => {
    if (hoverTimers.current.enter) {
      clearTimeout(hoverTimers.current.enter);
      hoverTimers.current.enter = null;
    }
    hoverTimers.current.leave = setTimeout(() => setIsHovered(false), 200);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (hoverTimers.current.enter) clearTimeout(hoverTimers.current.enter);
      if (hoverTimers.current.leave) clearTimeout(hoverTimers.current.leave);
    };
  }, []);

  useEffect(() => {
    try { localStorage.setItem('sidebarPinned', isSidebarPinnedOpen ? '1' : '0'); } catch { }
  }, [isSidebarPinnedOpen]);


  useEffect(() => {
    if (guestMode) return; // Skip auth wiring in guest mode
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setAuthLoading(true);
      if (currentUser) {
        setUser(currentUser);
        const userDocRef = doc(db, `artifacts/${appId}/users/${currentUser.uid}`);
        const userDocSnap = await getDoc(userDocRef);

        if (!userDocSnap.exists()) {
          const newUserProfile = {
            uid: currentUser.uid,
            email: currentUser.email,
            displayName: currentUser.displayName,
            photoURL: currentUser.photoURL,
            createdAt: serverTimestamp(),
            role: 'basic'
          };
          await setDoc(userDocRef, newUserProfile);
          setUserData(newUserProfile);
        } else {
          setUserData(userDocSnap.data());
        }

        const resumeDocRef = doc(db, `artifacts/${appId}/users/${currentUser.uid}/resume/data`);
        const resumeDocSnap = await getDoc(resumeDocRef);
        if (resumeDocSnap.exists() && resumeDocSnap.data().personalInfo?.name) {
          setIsUploadModalOpen(false);
        } else {
          setIsUploadModalOpen(true);
        }
      } else {
        setUser(null);
        setUserData(null);
        setIsUploadModalOpen(false);
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, [guestMode]);

  useEffect(() => {
    if (guestMode) return;
    if (user) {
      const resumeDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/resume/data`);
      const unsubscribe = onSnapshot(resumeDocRef, (docSnap) => {
        if (docSnap.exists()) {
          setResumeData(docSnap.data());
        } else {
          setResumeData(null);
        }
      });
      return () => unsubscribe();
    }
  }, [user, guestMode]);

  // Foreground time tracker (per user) stored in localStorage and broadcast via 'usage-updated'
  useEffect(() => {
    if (!user) return;
    let last = Date.now();
    const key = `usage:${appId}:${user.uid}`;
    const load = () => {
      try { return JSON.parse(localStorage.getItem(key)) || { totalMs: 0, days: {} }; }
      catch { return { totalMs: 0, days: {} }; }
    };
    let data = load();

    const tick = () => {
      const now = Date.now();
      if (document.visibilityState !== 'visible') { last = now; return; }
      const delta = now - last;
      last = now;
      if (!document.hasFocus()) return;
      const add = Math.min(Math.max(delta, 0), 60000);
      const day = new Date().toISOString().slice(0, 10);
      data.totalMs += add;
      data.days[day] = (data.days[day] || 0) + add;
      try { localStorage.setItem(key, JSON.stringify(data)); } catch { }
      window.dispatchEvent(new Event('usage-updated'));
    };

    const onFocus = () => { last = Date.now(); };
    const onVisibility = () => { last = Date.now(); };

    const interval = setInterval(tick, 15000);
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user]);

  const handleDataChange = async (section, data) => {
    if (user) {
      const resumeDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/resume/data`);
      const sanitized = deepSanitize({ [section]: data });
      await setDoc(resumeDocRef, sanitized, { merge: true });
    }
  };

  const handleResumeParse = async (parsedData) => {
    if (user) {
      const resumeDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/resume/data`);
      const validatedData = {
        personalInfo: parsedData.personalInfo || { name: user.displayName || '', email: user.email || '', phone: '', location: '' },
        goal: parsedData.goal || '',
        experience: Array.isArray(parsedData.experience) ? parsedData.experience : [],
        education: Array.isArray(parsedData.education) ? parsedData.education : [],
        projects: Array.isArray(parsedData.projects) ? parsedData.projects : [],
        skills: Array.isArray(parsedData.skills) ? parsedData.skills : [],
        achievements: Array.isArray(parsedData.achievements) ? parsedData.achievements : [],
        awards: Array.isArray(parsedData.awards) ? parsedData.awards : [],
        certificates: Array.isArray(parsedData.certificates) ? parsedData.certificates : [],
        hobbies: Array.isArray(parsedData.hobbies) ? parsedData.hobbies : [],
      };
      await setDoc(resumeDocRef, deepSanitize(validatedData));
    }
  };

  const initiateAnalysisFromBuilder = () => {
    setAnalysisTrigger({ source: 'builder', timestamp: Date.now() });
    setActiveTab('resumeScoring');
  };

  const handleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error signing in with Google:", error);
    }
  };

  const handleSignOut = () => {
    signOut(auth).catch((error) => console.error("Sign out error", error));
    setGuestMode(false);
    try { localStorage.removeItem('guestMode'); } catch { }
  };

  const handleInitiateCoverLetter = (jobDescription) => {
    setCoverLetterJd(jobDescription);
    setActiveTab('coverLetter');
  };

  const handleClearCoverLetterJd = () => setCoverLetterJd('');

  const handleCareerTabChange = (tab) => {
    setCareerActiveTab(tab);
    if (tab === 'basic') {
      setActiveTab('careerPath');
    } else if (tab === 'advanced') {
      setActiveTab('careerCopilot');
    }
  };

  const handleInitiateInterviewQuestions = (jobDescription) => {
    setInterviewQuestionsJd(jobDescription);
    setActiveTab('interviewQuestions');
  };

  const handleGenerateInterviewQuestions = async (jobDescription) => {
    setIsInterviewQuestionsModalOpen(true);
    setIsLoadingInterviewQuestions(true);
    setInterviewQuestionsData(null);

    const formData = new FormData();
    formData.append('job_description_text', jobDescription);
    formData.append('resume_text', formatResumeDataAsText(resumeData));

    try {
      const response = await fetch(`${API_BASE_URL}/generate-qna`, { method: 'POST', body: formData });
      if (!response.ok) throw new Error('Failed to generate questions.');
      const data = await response.json();
      setInterviewQuestionsData(data.qna_list);
    } catch (error) {
      console.error("Error generating interview questions:", error);
      setInterviewQuestionsData([{ question: "Error", answer: "Could not generate questions. Please try again." }]);
    } finally {
      setIsLoadingInterviewQuestions(false);
    }
  };


  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#fafafa]">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-[#3C0F6C] mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-[#3C0F6C]">Initializing Aspirant Suite...</p>
        </div>
      </div>
    );
  }

  if (!user && !guestMode) {
    const handleSkip = () => {
      setGuestMode(true);
      try { localStorage.setItem('guestMode', '1'); } catch { }
      setActiveTab('dashboard');
    };
    return <LoginScreen onSignIn={handleSignIn} onSkip={handleSkip} />;
  }

  if (!resumeData && !isUploadModalOpen) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#fafafa]">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 text-[#3C0F6C] mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 text-[#3C0F6C]">Loading your resume data...</p>
        </div>
      </div>
    );
  }



  return (
    <div className={`min-h-screen font-sans`}>
      <div className="flex flex-row min-h-screen">
        {/* Sidebar */}
        <aside
          onPointerEnter={handleSidebarMouseEnter}
          onPointerLeave={handleSidebarMouseLeave}
          className={`${isDrawerOpen ? 'w-64' : 'w-16'} fixed left-0 top-0 h-[100dvh] z-50 shrink-0 bg-[#3c0f6c] transition-all duration-300 ease-in-out overflow-y-auto group`}
          style={{ backdropFilter: 'blur(8px)', color: 'white', borderRight: '1px solid var(--rk-border)' }}
        >
          {/* Logo + Title + Toggle Button */}
          <div className="flex flex-col">
            <div className="flex items-center gap-2 min-w-0 px-3 py-3">
              <img
                src={rakfort}
                alt="Logo"
                className="w-8 h-8 object-contain flex-shrink-0"
              />
              <h1
                className={`text-2xl font-bold truncate transition-all duration-300 ease-in-out ${isDrawerOpen ? 'opacity-100 ml-2' : 'opacity-0 ml-0 pointer-events-none'}`}
                style={{ color: 'var(--color-accent)' }}
              >
                Aspirant Suite
              </h1>
            </div>
            <div className="flex justify-center px-3 py-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSidebarPinnedOpen(v => !v);
                }}
                className={`flex items-center justify-center w-8 h-8 rounded-full bg-[#3c0f6c] border border-[#5d3a8f] shadow-md hover:bg-[#4d1a8c] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#FFD600] ${isDrawerOpen ? 'rotate-180' : ''}`}
                aria-label={isDrawerOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              >
                <svg
                  className="w-3.5 h-3.5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>


          {/* User Info with Profile Dropdown */}
          <div className="relative mb-8 px-2">
            <button
              onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              className="flex items-center gap-3 w-full p-2 rounded-xl hover:bg-[#fafafa]/10 transition-colors"
            >
              <img
                src={user.photoURL || 'https://placehold.co/40x40/1a4673/fafafa?text=A'}
                alt="User Avatar"
                className="w-10 h-10 rounded-full border border-[#d5e7f3] object-cover flex-shrink-0"
              />
              <div className={`min-w-0 flex-1 text-left transition-all duration-300 ease-in-out ${isDrawerOpen ? 'opacity-100 ml-1' : 'opacity-0 ml-0 pointer-events-none'}`}>
                <p className="text-sm font-semibold truncate">{user.displayName}</p>
                <p className="text-xs opacity-80 capitalize truncate">{userData?.role} User</p>
              </div>
              <span className={`${isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'} transition-opacity duration-300 ease-in-out`}>
                <Edit3 size={16} className="opacity-60" />
              </span>
            </button>

            {/* Profile Dropdown */}
            {showProfileDropdown && isDrawerOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white text-[#0E0E0E] rounded-xl shadow-lg border border-[#E5E5E5] z-50">
                <button
                  onClick={() => {
                    setActiveTab('resumeBuilder');
                    setShowProfileDropdown(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FFD600] hover:text-[#0E0E0E] rounded-t-xl"
                >
                  <User size={20} />
                  <span>AI Resume Builder</span>
                </button>
                <div className="border-t border-[#d5e7f3]">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#FFD600] hover:text-[#0E0E0E] rounded-b-xl"
                  >
                    <LogOut size={20} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav className="space-y-4 flex-grow min-w-0 overflow-x-hidden">
            {/* Dashboard Section */}

            <div>
              <NavItem icon={<MessageSquare size={20} />} label="Dashboard" showText={isDrawerOpen} active={activeTab === 'dashboard'} onClick={() => setActiveTab('dashboard')} isDarkMode={isDarkMode} />
            </div>

            {/* Career Section */}
            <div>
              <div className={`text-xs font-semibold text-white/80 uppercase tracking-wider mb-2 px-4 transition-opacity duration-300 ease-in-out ${isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>Career</div>
              <div className="space-y-1">
                <NavItem icon={<MapPin size={20} />} label="Map Analysis" showText={isDrawerOpen} active={activeTab === 'careerPath'} onClick={() => handleCareerTabChange('basic')} isDarkMode={isDarkMode} />
                <NavItem icon={<Brain size={20} />} label="Career Copilot" showText={isDrawerOpen} active={activeTab === 'careerCopilot'} onClick={() => handleCareerTabChange('advanced')} isDarkMode={isDarkMode} />
              </div>
            </div>

            {/* Interview Section */}
            <div>
              <div className={`text-xs font-semibold text-[#fafafa]/60 uppercase tracking-wider mb-2 px-4 transition-opacity duration-300 ease-in-out ${isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>Interview</div>
              <div className="space-y-1">
                <NavItem icon={<Sparkles size={20} />} label="Interview Copilot" showText={isDrawerOpen} active={activeTab === 'playground'} onClick={() => setActiveTab('playground')} isDarkMode={isDarkMode} />
                <NavItem icon={<Lightbulb size={20} />} label="Interview AI Questions" showText={isDrawerOpen} active={activeTab === 'interviewQuestions'} onClick={() => setActiveTab('interviewQuestions')} isDarkMode={isDarkMode} />
                <NavItem icon={<Play size={20} />} label="Mock Interview" showText={isDrawerOpen} active={activeTab === 'interviewPrep'} onClick={() => setActiveTab('interviewPrep')} isDarkMode={isDarkMode} />
              </div>
            </div>

            {/* Tools Section */}
            <div>
              <div className={`text-xs font-semibold text-[#fafafa]/60 uppercase tracking-wider mb-2 px-4 transition-opacity duration-300 ease-in-out ${isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>Tools</div>
              <div className="space-y-1">
                <NavItem icon={<FileText size={20} />} label="Cover Letter" showText={isDrawerOpen} active={activeTab === 'coverLetter'} onClick={() => setActiveTab('coverLetter')} isDarkMode={isDarkMode} />
                <NavItem icon={<BarChart2 size={20} />} label="Resume Scoring" showText={isDrawerOpen} active={activeTab === 'resumeScoring'} onClick={() => setActiveTab('resumeScoring')} isDarkMode={isDarkMode} />
                <NavItem icon={<Target size={20} />} label="Job Matching" showText={isDrawerOpen} active={activeTab === 'jobMatching'} onClick={() => setActiveTab('jobMatching')} isDarkMode={isDarkMode} />
                {/* <NavItem icon={<Target size={20} />} label="Job Matching" showText={(isSidebarPinnedOpen || isSidebarHovered)} active={activeTab === 'jobMatching'} onClick={() => setActiveTab('jobMatching')} isDarkMode={isDarkMode} /> */}
              </div>
            </div>
            <NavItem icon={<Tag size={20} />} label="Pricing" showText={isDrawerOpen} active={activeTab === 'pricing'} onClick={() => setActiveTab('pricing')} isDarkMode={isDarkMode} />
          </nav>

          {/* Dark Mode Toggle */}
          <div className="mt-8 flex-shrink-0">
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className="w-full text-sm font-semibold py-2.5 rounded-xl transition-colors border"
              style={{ backgroundColor: 'var(--color-accent)', color: '#0E0E0E', borderColor: 'var(--color-accent)' }}
            >
              <div className="flex items-center justify-center gap-2">
                {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
                <span className={`hidden ${(isDrawerOpen ? 'md:inline' : 'md:hidden')}`}>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
              </div>
            </button>
          </div>
        </aside>

        {/* Main */}
        <main className={`flex-1 p-6 md:p-8 min-w-0`} style={{ backgroundColor: 'var(--color-muted)' }}>
          <div className="max-w-6xl mx-auto">
            {/* <div className={`mb-6 p-4 rounded-xl border bg-white dark:bg-[#5b3a7a]`} style={{ borderColor: 'var(--rk-border)' }}>
              <p className={`text-sm`}>
                Welcome back, <span className="font-semibold">{user.displayName?.split(' ')[0] || 'there'}</span> — continue your progress below.
              </p>
            </div> */}
            <MainContent
              activeTab={activeTab}
              resumeData={resumeData}
              userData={userData}
              user={user}
              db={db}
              appId={appId}
              analysisTrigger={analysisTrigger}
              coverLetterJd={coverLetterJd}
              interviewQuestionsJd={interviewQuestionsJd}
              onDataChange={handleDataChange}
              onAnalyze={initiateAnalysisFromBuilder}
              onParseComplete={handleResumeParse}
              onCareerTabChange={handleCareerTabChange}
              onGenerateCoverLetter={handleInitiateCoverLetter}
              onGenerateInterviewQuestions={handleInitiateInterviewQuestions}
              onClearCoverLetterJd={handleClearCoverLetterJd}
              setActiveTab={setActiveTab}
            />
          </div>
        </main>
      </div>

      {
        isInterviewQuestionsModalOpen && (
          <InterviewQuestionsModal
            isOpen={isInterviewQuestionsModalOpen}
            onClose={() => setIsInterviewQuestionsModalOpen(false)}
            data={interviewQuestionsData}
            isLoading={isLoadingInterviewQuestions}
          />)
      }

      <ResumeUploadModal
        isOpen={isUploadModalOpen}
        onUploadComplete={() => setIsUploadModalOpen(false)}
        onParseComplete={handleResumeParse}
        onSignOut={handleSignOut}
      />
    </div >
  );
};

const NavItem = ({ icon, label, sublabel, active, onClick, isDarkMode = false, showText = true }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors border min-w-0 ${active
      ? 'bg-[#FFD600] text-[#0E0E0E] border-[#FFD600] shadow'
      : 'text-white hover:bg-white/10 hover:text-white border-transparent'
      }`}
  >
    <span className="flex-shrink-0">{icon}</span>
    <div className={`${showText ? 'flex-1' : 'hidden md:block md:flex-1 md:opacity-100'} min-w-0 ${showText ? '' : 'md:hidden'}`}>
      <div className="truncate">{label}</div>
      {sublabel && (
        <div className={`text-xs truncate ${active ? 'text-[#0E0E0E]/70' : 'text-white/80'
          }`}>
          {sublabel}
        </div>
      )}
    </div>
  </button>
);

const InterviewQuestionsModal = ({ isOpen, onClose, data, isLoading }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-[#3C0F6C]/70 flex items-center justify-center p-4 z-50">
      <div className="bg-[#fafafa] border border-[#d5e7f3] rounded-2xl shadow-2xl max-w-2xl w-full flex flex-col max-h-[90vh]">
        <div className="p-6 border-b border-[#d5e7f3] flex-shrink-0 flex  items-center">
          <h3 className="text-xl font-bold text-[#3C0F6C]">AI-Generated Interview Prep</h3>
          <button onClick={onClose} className="p-1 rounded-full text-[#3C0F6C] hover:bg-[#d5e7f3]">
            <XCircle size={20} />
          </button>
        </div>
        <div className="p-6 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <svg className="animate-spin h-8 w-8 text-[#3C0F6C]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="ml-4 text-[#3C0F6C]">Generating questions and answers...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {(data || []).map((item, index) => (
                <div key={index} className="rounded-xl border border-[#d5e7f3] p-4 bg-[#d5e7f3]/30">
                  <h4 className="font-bold text-[#3C0F6C] mb-2">Question {index + 1}:</h4>
                  <p className="mb-2 text-[#3C0F6C]">{item.question}</p>
                  <h5 className="font-semibold text-[#3C0F6C]/80 mb-1">Example Answer:</h5>
                  <p className="text-[#3C0F6C] whitespace-pre-wrap bg-[#fafafa] border border-[#d5e7f3] p-3 rounded-lg">{item.answer}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const App = () => {
  return (
    <Router>
      <Routes>
        {/* Marketing/landing routes wrapped with global Layout */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/jobsuite" element={<Layout><Desktop20  /></Layout>} />
                <Route path="/privacypolicy" element={<Layout><PrivacyPolicy  /></Layout>} />

             
        {/* <Route path="/features" element={<Layout><Desktop21 /></Layout>} /> */}
        {/* <Route path="/ai-tools" element={<Layout><Desktop24 /></Layout>} /> */}
        <Route path="/jobsuite/resume-builder" element={<Layout><Desktop4 /></Layout>} />
        <Route path="/jobsuite/interview-prep" element={<Layout><Desktop22 /></Layout>} />
        <Route path="/jobsuite/interview-simulation" element={<Layout><Desktop29 /></Layout>} />
        <Route path="/jobsuite/cover-letter-generator" element={<Layout><Desktop23 /></Layout>} />
        <Route path="/jobsuite/job-matching" element={<Layout><Desktop25 /></Layout>} />
        <Route path="/jobsuite/resume-scoring" element={<Layout><Desktop26 /></Layout>} />
        <Route path="jobsuite/signuppricing" element={<Layout><SignupPricing /></Layout>} />
        <Route path="jobsuite/blogs" element={<Layout><Blogs /></Layout>} />
        <Route path="jobsuite/blogs/:slug" element={<Layout><BlogDetail /></Layout>} />
        <Route path="jobsuite/contactus" element={<Layout><Desktop30 /></Layout>} />
                <Route path="/contactus" element={<NoviContact/>} />

        <Route path="jobsuite/terms" element={<Terms />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/privacypolicy" element={<Layout><PrivacyPolicy /></Layout>} />
        <Route path="/termsconditions" element={<Layout><TermsConditionsMain /></Layout>} />
        <Route path="/cancellationrefund" element={<Layout><CancellationRefund /></Layout>} />  

        {/* Main App Route (product experience) */}

           <Route path="/studysuite" element={<StudyLayout><Desktop20 suite="studysuite" /></StudyLayout>} />
             <Route path="/studysuite/signuppricing" element={<StudyLayout><SignupPricing suite="studysuite" /></StudyLayout>} />
                <Route path="/studysuite/interview-prep" element={<StudyLayout><Desktop22 suite="studysuite" /></StudyLayout>} />
              <Route path="/studysuite/resume-builder" element={<StudyLayout><Desktop4 suite="studysuite" /></StudyLayout>} />
              <Route path="studysuite/blogs" element={<StudyLayout><Blogs suite="studysuite" /></StudyLayout>} />
               <Route path="studysuite/blogs/:slug" element={<StudyLayout><BlogDetail suite="studysuite"/></StudyLayout>} />

      </Routes>
    </Router>
  );
};

export default App;